///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, OnInit, signal } from '@angular/core';
import { CustomerLocationDTO, CustomerLocationExtendedDTO } from '../../../generated/shared-types.model';
import { GtthCustomerLocationService } from '../../../services/customer/customer-location.service';
import { MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { finalize, Subject, takeUntil } from 'rxjs';
import { SuiTabsComponent, WindeSuiTab } from '../../../shared/sui-lib-customisations/sui-tabs/sui-tabs.component';
import { SuiCustomerLocationDTO } from '../../../generated/shared-sui-types.model';
import { SuiSelectControlComponent, SuiStringControlComponent, SuiTypeSelect } from '@siklu/sui-lib/infrastructure';
import { SuiLoadingDirective, SuiSvgIconComponent, SuiTranslatePipe } from '@siklu/sui-lib';
import { ContextSensitiveHelpComponent } from '../../../shared/context-sensitive-help/ctx-sensitive-help.component';
import { ButtonsDirectivesModule } from '../../../shared/directives/buttons-directives/buttons-directives.module';
import { ActionIconDirectivesModule } from '../../../shared/directives/action-icon-directives/action-icon-directives.module';
import { Currency } from '../../../services/customer/currencies-list';
import { isDefined } from '@siklu/sui-lib/utils';
import { GtthShareComponentsModule } from '../../../shared/shared-components.module';
import { GridDirectivesModule } from '../../../shared/directives/grid-directives/grid-directives.module';

@Component({
    selector: 'winde-customer-location-dialog',
    templateUrl: './customer-location-dialog.component.html',
    imports: [
        SuiLoadingDirective,
        MatDialogTitle,
        ContextSensitiveHelpComponent,
        ButtonsDirectivesModule,
        GridDirectivesModule,
        ActionIconDirectivesModule,
        SuiSvgIconComponent,
        MatDialogClose,
        MatDialogContent,
        SuiTabsComponent,
        SuiStringControlComponent,
        SuiTranslatePipe,
        SuiSelectControlComponent,
        GtthShareComponentsModule
    ],
    styleUrls: ['customer-location.scss']
})
export class CustomerLocationDialogComponent implements OnInit {
    locations = signal<WindeSuiTab<SuiCustomerLocationDTO>[]>([]);
    activeLocation: WindeSuiTab<SuiCustomerLocationDTO>;

    editMode = false;
    showDeleteConfirmation = false;

    locationExtended: CustomerLocationExtendedDTO;
    loaderKey = LoaderIds.CUSTOMERS_LOCATION_DIALOG_LOADER_ID;

    currencySelect: SuiTypeSelect<Currency>;

    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        public customerLocationService: GtthCustomerLocationService,
        public dialogRef: MatDialogRef<CustomerLocationDialogComponent>,
        public preloaderService: PreloaderService
    ) {
        const updatedCurrencies = [...this.customerLocationService.getCurrenciesList()];
        updatedCurrencies.forEach((currency) => (currency.name += ` (${currency.symbol})`));
        this.currencySelect = new SuiTypeSelect(undefined, updatedCurrencies, 'name');
        this.currencySelect.isFilterable = true;
    }

    ngOnInit() {
        this.preloaderService.show(this.loaderKey);
        this.customerLocationService
            .getAvailableLocations()
            .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
            .subscribe((res) => {
                const tabs = res.map((value) => new SuiCustomerLocationDTO(value)).map((location) => this.createTabFromObject(location));
                this.locations.set(tabs);
                this.selectLocation(this.locations()[0]);
            });
    }

    selectLocation(location: WindeSuiTab<SuiCustomerLocationDTO>) {
        this.editMode = false;
        this.showDeleteConfirmation = false;
        this.activeLocation?.content.name.restoreInitialState();
        this.activeLocation = location;

        this.preloaderService.show(this.loaderKey);
        this.customerLocationService
            .getLocationExtended(this.activeLocation.content.id)
            .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
            .subscribe((res) => {
                this.locationExtended = res;
            });
    }

    addNewLocation(): void {
        this.activeLocation = this.createTabFromObject(new SuiCustomerLocationDTO({} as CustomerLocationDTO));
        this.openEditLocation();
    }

    openEditLocation(): void {
        const selectedCurrency = this.currencySelect.options.find((currency) => currency.code === this.activeLocation.content.currency);
        this.currencySelect.setValue(isDefined(selectedCurrency) ? selectedCurrency : this.currencySelect.options[0]);
        this.editMode = true;
    }

    discardChanges(): void {
        if (isDefined(this.activeLocation.content.id)) {
            this.selectLocation(this.activeLocation);
        } else {
            this.selectLocation(this.locations()[0]);
        }
    }

    saveLocation(): void {
        if (!this.activeLocation.content.validateAndShowError()) {
            return;
        }
        const saveLocation = this.activeLocation.content.toDto();
        saveLocation.currency = this.currencySelect.getValue().code;

        if (!saveLocation.id) {
            saveLocation.id = 'LOCATION_' + saveLocation.name.toUpperCase().replace(/[^A-Z\d]/gi, '');
        }

        this.customerLocationService.save(saveLocation).subscribe((res) => {
            const newTab = this.createTabFromObject(new SuiCustomerLocationDTO(res));

            this.locations.update((tabs) => {
                const index = tabs.findIndex((tab) => tab.content.id === newTab.content.id);

                if (index >= 0) {
                    tabs[index] = newTab;
                } else {
                    tabs.push(newTab);
                }
                return tabs;
            });

            this.selectLocation(newTab);
        });
    }

    deleteLocation(): void {
        this.customerLocationService.delete(this.activeLocation.content.id).subscribe(() => {
            this.locations.update((tabs) => {
                return tabs.filter((tab) => tab.content.id !== this.activeLocation.content.id);
            });

            this.selectLocation(this.locations()[0]);
        });
    }

    private createTabFromObject(object: SuiCustomerLocationDTO): WindeSuiTab<SuiCustomerLocationDTO> {
        const tab = {
            title: signal<string>(object.name.getValue()),
            content: object
        } as WindeSuiTab<SuiCustomerLocationDTO>;

        object.name.validators.push(() => {
            const message = 'Name must be unique';
            const isNotValid = this.locations().some(
                (someTab) => someTab !== tab && someTab.content.name.getValue().toLowerCase() === tab.content.name.getValue().toLowerCase()
            );
            return {
                isEnabled: isNotValid,
                isActivated: isNotValid,
                translationPath: isNotValid ? message : null
            };
        });

        object.name.valueChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            tab.title.set(object.name.getValue());
        });

        return tab;
    }
}
